import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  CalendarIcon,
  UserGroupIcon,
  BanknotesIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import type { Tournament } from './TournamentList';

interface TournamentCardProps {
  tournament: Tournament;
  onEdit: (tournament: Tournament) => void;
  onDelete: (tournament: Tournament) => void;
  onViewDetails?: (tournament: Tournament) => void;
}

const STATUS_CONFIG: Record<string, { label: string; dot: string; badge: string }> = {
  upcoming:  { label: 'Upcoming',  dot: 'bg-blue-500',  badge: 'bg-blue-50 text-blue-700 border border-blue-200' },
  ongoing:   { label: 'Ongoing',   dot: 'bg-green-500', badge: 'bg-green-50 text-green-700 border border-green-200' },
  completed: { label: 'Completed', dot: 'bg-gray-400',  badge: 'bg-gray-100 text-gray-600 border border-gray-200' },
  cancelled: { label: 'Cancelled', dot: 'bg-red-500',   badge: 'bg-red-50 text-red-700 border border-red-200' },
};

const ACCENT_COLOR: Record<string, string> = {
  upcoming:  'bg-blue-500',
  ongoing:   'bg-golf-green',
  completed: 'bg-gray-400',
  cancelled: 'bg-red-500',
};

export default function TournamentCard({
  tournament,
  onEdit,
  onDelete,
  onViewDetails,
}: TournamentCardProps) {
  const statusConfig = STATUS_CONFIG[tournament.status] ?? STATUS_CONFIG.upcoming;
  const accentColor = ACCENT_COLOR[tournament.status] ?? 'bg-gray-400';

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group"
    >
      <div className={`h-1 w-full ${accentColor}`} />

      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="text-xl font-bold text-golf-gray leading-tight">{tournament.name}</h3>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full capitalize ${statusConfig.badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
                {statusConfig.label}
              </span>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-gray-500">
              <div className="flex items-center gap-1.5">
                <CalendarIcon className="w-4 h-4 flex-shrink-0" />
                <span>
                  {format(new Date(tournament.start_date), 'MMM d')} – {format(new Date(tournament.end_date), 'MMM d, yyyy')}
                </span>
              </div>
              {tournament.venue && (
                <div className="flex items-center gap-1.5">
                  <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate max-w-[200px]">{tournament.venue}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => onEdit(tournament)}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              aria-label="Edit tournament"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(tournament)}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              aria-label="Delete tournament"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
                <UserGroupIcon className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">Capacity</span>
            </div>
            <p className="text-lg font-bold text-golf-gray leading-tight">
              {tournament.capacity}
              <span className="text-xs font-normal text-gray-400 ml-1">Players</span>
            </p>
          </div>

          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-md bg-green-100 flex items-center justify-center flex-shrink-0">
                <BanknotesIcon className="w-3.5 h-3.5 text-green-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">Reg. Fee</span>
            </div>
            <p className="text-base font-bold text-golf-gray leading-tight tabular-nums">
              {formatCurrency(tournament.registration_fee)}
            </p>
          </div>

          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
                <BanknotesIcon className="w-3.5 h-3.5 text-amber-600" />
              </div>
              <span className="text-xs text-gray-500 font-medium">Prize Pool</span>
            </div>
            <p className="text-base font-bold text-golf-gray leading-tight tabular-nums">
              {formatCurrency(tournament.prize_pool)}
            </p>
          </div>
        </div>

        {tournament.description && (
          <p className="text-sm text-gray-500 line-clamp-2 mb-5 leading-relaxed">
            {tournament.description}
          </p>
        )}

        <div className="flex justify-end">
          <button
            onClick={() => onViewDetails && onViewDetails(tournament)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-golf-orange border border-golf-orange/30 rounded-lg hover:bg-golf-orange hover:text-white transition-all duration-200 group-hover:border-golf-orange"
          >
            View Details
            <ArrowRightIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
